import { CMS_ATTR, parseCMSListQueryFromAttributes, buildCMSListKey, parseCMSListMode } from '@superfunnel/schemas/content-library';

/**
 * Content Library binding extraction.
 *
 * The analog of `getFormIdsInWebsiteData`: scans a website's HTML for CMS
 * bindings and returns the precomputed references (inline single-entry bindings
 * and dynamic list containers, tagged with their freshness `mode`). Recomputed
 * on every content change (editor save and agent edits) and stored on
 * `WebsiteData.cmsRefs`, so publish and live-render consumers can resolve CMS
 * data without re-scanning HTML.
 *
 * Both binding shapes co-locate the collection id with the entry/list attribute
 * on the same element (see the agent authoring spec), so a flat per-tag scan is
 * sufficient — no DOM tree is needed.
 */
/** Matches an opening/self-closing tag and captures its raw attribute string. */
const TAG_RE = /<([a-zA-Z][\w-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;
/** Matches a single `name` or `name="value"` / `name='value'` attribute. */
const ATTR_RE = /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
/**
 * Decode the HTML entities a DOM serializer emits inside attribute values.
 * Required because attributes are stored serialized (the dashboard's
 * `doc.body.innerHTML` and node-html-parser encode `"` in an attribute value as
 * `&quot;`), so values carrying JSON — notably `data-sf-cms-filter` — arrive
 * entity-encoded. Decoding here keeps this regex scanner consistent with the
 * DOM/cheerio/html-react-parser parsers the renderers use, so the list key
 * (which includes the filter) matches across publish, serve, and editor preview.
 * `&amp;` is decoded last so `&amp;quot;` resolves to `&quot;`, not `"`.
 */
function decodeHtmlEntities(value) {
    if (!value.includes('&')) {
        return value;
    }
    return (value
        .replace(/&quot;/g, '"')
        .replace(/&#0*34;/g, '"')
        // eslint-disable-next-line quotes
        .replace(/&apos;/g, "'")
        // eslint-disable-next-line quotes
        .replace(/&#0*39;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&'));
}
/** Parse a tag's raw attribute string into a lowercased-name attribute map. */
function parseTagAttribs(rawAttribs) {
    const attribs = {};
    let match;
    ATTR_RE.lastIndex = 0;
    while ((match = ATTR_RE.exec(rawAttribs)) !== null) {
        const name = match[1].toLowerCase();
        const value = match[2] ?? match[3] ?? match[4] ?? '';
        attribs[name] = decodeHtmlEntities(value);
    }
    return attribs;
}
/**
 * Extract CMS bindings from a website's HTML (sections, banners, popups).
 * Dedupes entries by `mode:collectionId/entryId` and lists by `mode:listKey`.
 */
function getCMSRefsInWebsiteData(website) {
    const entries = [];
    const lists = [];
    const seenEntries = new Set();
    const seenLists = new Set();
    const htmls = [];
    const pushIfHtml = (value) => {
        // data-sf-cms-collection is present for both inline entries and lists.
        if (typeof value === 'string' && value.includes(CMS_ATTR.collection)) {
            htmls.push(value);
        }
    };
    for (const section of website.sections ?? []) {
        pushIfHtml(section?.content);
    }
    for (const banner of website.banners ?? []) {
        pushIfHtml(banner?.content);
    }
    for (const popup of website.popups ?? []) {
        pushIfHtml(popup?.content);
    }
    for (const html of htmls) {
        let tagMatch;
        TAG_RE.lastIndex = 0;
        while ((tagMatch = TAG_RE.exec(html)) !== null) {
            const attribs = parseTagAttribs(tagMatch[2]);
            const collectionId = attribs[CMS_ATTR.collection]?.trim();
            if (!collectionId) {
                continue;
            }
            // Dynamic list container (data-sf-list + collection co-located).
            if (attribs[CMS_ATTR.list] !== undefined) {
                const query = parseCMSListQueryFromAttributes(attribs);
                const listKey = buildCMSListKey(collectionId, query);
                const mode = parseCMSListMode(attribs);
                const dedupeKey = `${mode}:${listKey}`;
                if (!seenLists.has(dedupeKey)) {
                    seenLists.add(dedupeKey);
                    lists.push({ collectionId, query, listKey, mode });
                }
                continue;
            }
            // Inline single-entry binding (data-sf-cms-entry + collection co-located).
            const entryId = attribs[CMS_ATTR.entry]?.trim();
            if (entryId) {
                const mode = parseCMSListMode(attribs);
                const dedupeKey = `${mode}:${collectionId}/${entryId}`;
                if (!seenEntries.has(dedupeKey)) {
                    seenEntries.add(dedupeKey);
                    entries.push({ collectionId, entryId, mode });
                }
            }
        }
    }
    return { entries, lists };
}

export { getCMSRefsInWebsiteData };
//# sourceMappingURL=content-library.esm.js.map
